import { isLikelyMachineGeneratedHandle, RESERVED_HANDLES } from '@/lib/public-handle';

export const INSTITUTION_HANDLE_MIN = 3;
export const INSTITUTION_HANDLE_MAX = 60;
export const INSTITUTION_PUBLIC_SETUP_PATH = '/institution-dashboard/settings#public-profile';
export const DEMO_INSTITUTION_LEGACY_HANDLE = 'demo.institut.be308cf5cb3241e5';
export const DEMO_INSTITUTION_HANDLE = 'demo-institution';

const INSTITUTION_HANDLE_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const INSTITUTION_REFERENCE_PATTERN = /^INST-[A-Z0-9]+(?:-[A-Z0-9]+)+$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const UUID_FRAGMENT_PATTERN = /(?:^|-)[0-9a-f]{8,}(?:-|$)/i;

export type InstitutionHandleValidation =
  | { ok: true; handle: string }
  | { ok: false; error: string };

export type InstitutionPublicIdentity = {
  publicHandle?: string | null;
  institutionReference?: string | null;
  isPublished?: boolean;
  public_handle?: string | null;
  institution_id?: string | null;
};

const LEGAL_ORGANIZATION_SUFFIXES = new Set([
  'co',
  'company',
  'corp',
  'corporation',
  'gmbh',
  'inc',
  'incorporated',
  'limited',
  'llc',
  'llp',
  'ltd',
  'plc',
]);
const GENERIC_BRAND_DESCRIPTORS = new Set([
  'group',
  'holding',
  'holdings',
  'service',
  'services',
  'solution',
  'solutions',
  'tech',
  'technology',
  'technologies',
]);

function institutionNameWords(name: string): string[] {
  const words = (name || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  while (words.length > 1 && LEGAL_ORGANIZATION_SUFFIXES.has(words.at(-1) || '')) words.pop();
  return words;
}

export function institutionHandleCandidates(name: string): string[] {
  const words = institutionNameWords(name);
  const usesBrandRoot =
    words.length > 1 && words.slice(1).every((word) => GENERIC_BRAND_DESCRIPTORS.has(word));
  const baseWords = usesBrandRoot ? words.slice(0, 1) : words;
  const primary = baseWords.join('-');
  const candidates = [primary];

  if (
    usesBrandRoot &&
    words.slice(1).some((word) => /^(tech|technology|technologies)$/.test(word))
  ) {
    candidates.push(`${words[0]}-tech`);
  }

  return [...new Set(candidates)]
    .map((candidate) => candidate.slice(0, INSTITUTION_HANDLE_MAX).replace(/-+$/g, ''))
    .filter((candidate) => validateInstitutionHandle(candidate).ok);
}

export function institutionHandleFromName(name: string): string {
  const candidate = institutionHandleCandidates(name)[0];
  const base = candidate || institutionNameWords(name).join('-').slice(0, INSTITUTION_HANDLE_MAX);

  if (
    base.length < INSTITUTION_HANDLE_MIN ||
    RESERVED_HANDLES.has(base) ||
    UUID_PATTERN.test(base) ||
    UUID_FRAGMENT_PATTERN.test(base)
  ) {
    return 'institution-profile';
  }
  return base;
}

export function sanitizeInstitutionHandleInput(raw: string): string {
  return (raw || '')
    .trim()
    .toLowerCase()
    .replace(/^@+/, '')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .slice(0, INSTITUTION_HANDLE_MAX);
}

export function validateInstitutionHandle(raw: string): InstitutionHandleValidation {
  const handle = (raw || '').trim().toLowerCase().replace(/^@+/, '');
  if (!handle) return { ok: false, error: 'Handle is required.' };
  if (handle.length < INSTITUTION_HANDLE_MIN) {
    return {
      ok: false,
      error: `Handle must be at least ${INSTITUTION_HANDLE_MIN} characters.`,
    };
  }
  if (handle.length > INSTITUTION_HANDLE_MAX) {
    return {
      ok: false,
      error: `Handle must be at most ${INSTITUTION_HANDLE_MAX} characters.`,
    };
  }
  if (!INSTITUTION_HANDLE_PATTERN.test(handle)) {
    return {
      ok: false,
      error:
        'Use lowercase letters, numbers, and hyphens. The handle must start and end with a letter or number.',
    };
  }
  if (RESERVED_HANDLES.has(handle)) {
    return { ok: false, error: 'This handle is reserved. Choose another.' };
  }
  if (UUID_PATTERN.test(handle) || UUID_FRAGMENT_PATTERN.test(handle)) {
    return { ok: false, error: 'Public handles cannot contain database ID fragments.' };
  }
  return { ok: true, handle };
}

export async function allocateInstitutionHandle(
  institutionName: string,
  isAvailable: (candidate: string) => Promise<boolean>,
  options?: { maxAttempts?: number }
): Promise<string> {
  const readableCandidates = institutionHandleCandidates(institutionName);
  const base = readableCandidates[0] || institutionHandleFromName(institutionName);
  const maxAttempts = options?.maxAttempts ?? 999;

  for (const candidate of readableCandidates) {
    const validation = validateInstitutionHandle(candidate);
    if (validation.ok && (await isAvailable(validation.handle))) return validation.handle;
  }

  for (let attempt = 2; attempt <= maxAttempts + 1; attempt += 1) {
    const suffix = String(attempt);
    const trimmedBase = base.slice(0, INSTITUTION_HANDLE_MAX - suffix.length).replace(/-+$/g, '');
    const candidate = `${trimmedBase}${suffix}`;
    const validation = validateInstitutionHandle(candidate);
    if (validation.ok && (await isAvailable(validation.handle))) return validation.handle;
  }

  throw new Error('No readable institution handle is available for this name.');
}

export function resolveInstitutionLegacyHandle(raw: string): string | null {
  return raw.trim().toLowerCase() === DEMO_INSTITUTION_LEGACY_HANDLE
    ? DEMO_INSTITUTION_HANDLE
    : null;
}

export function isLikelyMachineGeneratedInstitutionHandle(handle: string): boolean {
  return isLikelyMachineGeneratedHandle(handle);
}

export function isInstitutionReference(value: string): boolean {
  return INSTITUTION_REFERENCE_PATTERN.test(value.trim());
}

function publicHandleFromIdentity(identity: InstitutionPublicIdentity): string | null {
  const validation = validateInstitutionHandle(
    identity.publicHandle ?? identity.public_handle ?? ''
  );
  return validation.ok ? validation.handle : null;
}

export type InstitutionPublicPathOptions = {
  /**
   * Marks the link as originating from the authenticated institution console, so the public
   * page can offer a "back to institution dashboard" link instead of the generic public
   * landing-page link. Only ever appended to the actual public-profile path, never to the
   * setup-path fallback, and only ever used for internal dashboard navigation, never for a
   * link that will be shared with a third party.
   */
  context?: 'institution-dashboard';
};

export function getInstitutionPublicPath(
  identity: InstitutionPublicIdentity,
  options?: InstitutionPublicPathOptions
): string {
  const publicHandle = publicHandleFromIdentity(identity);
  if (identity.isPublished === true && publicHandle) {
    const path = `/institution-public/${encodeURIComponent(publicHandle)}`;
    return options?.context === 'institution-dashboard'
      ? `${path}?from=institution-dashboard`
      : path;
  }
  return INSTITUTION_PUBLIC_SETUP_PATH;
}

/** Public-only path helper for places that already know publication was confirmed. */
export function institutionPublicPath(
  identity: InstitutionPublicIdentity,
  options?: InstitutionPublicPathOptions
): string | null {
  const publicHandle = publicHandleFromIdentity(identity);
  if (!publicHandle) return null;
  const path = `/institution-public/${encodeURIComponent(publicHandle)}`;
  return options?.context === 'institution-dashboard' ? `${path}?from=institution-dashboard` : path;
}

export function institutionPublicHostPath(
  identity: InstitutionPublicIdentity,
  host = 'ledger.ertzyx.com'
): string {
  return `${host}${getInstitutionPublicPath(identity)}`;
}

export function institutionSubmissionPath(identity: InstitutionPublicIdentity): string | null {
  const reference = (identity.institutionReference ?? identity.institution_id ?? '').trim();
  if (isInstitutionReference(reference)) {
    return `/submit/${encodeURIComponent(reference)}`;
  }
  const handle = publicHandleFromIdentity(identity);
  return handle ? `/submit/${encodeURIComponent(handle)}` : null;
}
